import { AccessTime, Autorenew, CheckCircle, EventAvailable, Inbox } from "@mui/icons-material";
import { Box, Button, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useRecordContext, useTranslate } from "react-admin";

import {
  ActivityRecommendationsDocument,
  ActivityRecommendationsQuery,
  ActivityRecommendationsQueryVariables,
  DataSource,
} from "../generated/graphql";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { client } from "../lib/apolloClient";
import { activityCategoryStyle, borderRadius, color, shadows, typography } from "../theme/designTokens";
import { PatientRecord } from "../types/patient";
import { ActivityCategoryIcon } from "./ActivityCategoryIcon";
import { ACTIVITY_GOAL_KINDS, ActivityGoalKind, ActivityGoalProgressByKind, GoalProgress } from "./activityGoalKinds";
import { ActivityRowHeader } from "./ActivityRowHeader";
import { ActivityThumbnail } from "./ActivityThumbnail";
import { ContentKindIcon } from "./ContentKindIcon";
import { PlanActivityDialog } from "./PlanActivityDialog";

type Recommendation = ActivityRecommendationsQuery["activityRecommendations"][number];

const RECOMMENDATION_LIMIT = 4;
const SECTION_WIDTH = 920;
const RECOMMENDATION_THUMBNAIL_WIDTH = 76;
const RECOMMENDATION_THUMBNAIL_HEIGHT = 54;

enum BlockStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
}

const RecommendationRow = ({
  recommendation,
  kind,
  isPlanned,
  onPlan,
}: {
  recommendation: Recommendation;
  kind: ActivityGoalKind;
  isPlanned: boolean;
  onPlan: () => void;
}) => {
  const translate = useTranslate();

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        mb: 1.25,
        border: `1px solid ${isPlanned ? color.plannedBorder : color.panelBorder}`,
        borderRadius: borderRadius.sm,
        backgroundColor: isPlanned ? color.plannedBackground : color.white,
        boxShadow: shadows.card,
      }}
      data-testid={`recommendation-row-${kind.toLowerCase()}`}
    >
      <ActivityRowHeader
        leading={
          <ActivityThumbnail
            thumbnailFilename={recommendation.thumbnailFilename ?? null}
            cmsContentKind={recommendation.cmsContentKind}
            width={RECOMMENDATION_THUMBNAIL_WIDTH}
            height={RECOMMENDATION_THUMBNAIL_HEIGHT}
          />
        }
        title={recommendation.title}
        titleWeight={typography.fontWeight.medium}
        titleIcon={<ContentKindIcon contentKind={recommendation.cmsContentKind} size={22} />}
        caption={
          <Chip
            size="small"
            icon={<AccessTime sx={{ fontSize: "16px !important" }} />}
            label={translate("activityGoals.recommendations.duration", { minutes: recommendation.durationMinutes })}
            sx={{
              height: 24,
              backgroundColor: color.background2,
              border: `1px solid ${color.datagridBorder}`,
              color: color.gray,
              fontWeight: 500,
            }}
          />
        }
        trailing={
          isPlanned ? (
            <Chip
              icon={<CheckCircle sx={{ fontSize: "18px !important", color: `${color.plannedText} !important` }} />}
              label={translate("activityGoals.recommendations.planned")}
              sx={{
                backgroundColor: color.plannedChipBackground,
                color: color.plannedText,
                border: `1px solid ${color.plannedChipBorder}`,
                fontWeight: 500,
              }}
              data-testid={`recommendation-planned-${recommendation.cmsContentId}`}
            />
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<EventAvailable sx={{ fontSize: typography.fontSize.md }} />}
              onClick={onPlan}
              data-testid={`recommendation-plan-${recommendation.cmsContentId}`}
              // Cross-app E2E matches the planned activity in the senior app by content
              // uuid (the admin cmsContentId is a catalogue index, not the shared key).
              data-content-uuid={recommendation.contentUuid}
              sx={{ backgroundColor: color.primary, color: color.white, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {translate("activityGoals.recommendations.plan")}
            </Button>
          )
        }
      />
    </Box>
  );
};

const RecommendationSkeleton = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 1.5,
      py: 0.75,
      mb: 1.25,
      border: `1px solid ${color.panelBorder}`,
      borderRadius: borderRadius.sm,
      boxShadow: shadows.card,
    }}
  >
    <Skeleton
      variant="rounded"
      width={RECOMMENDATION_THUMBNAIL_WIDTH}
      height={RECOMMENDATION_THUMBNAIL_HEIGHT}
      sx={{ borderRadius: borderRadius.sm }}
    />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="52%" height={18} />
      <Skeleton variant="text" width="30%" height={14} />
    </Box>
    <Skeleton variant="rounded" width={88} height={36} />
  </Box>
);

const EmptyRecommendations = ({ onRegenerate }: { onRegenerate: () => void }) => {
  const translate = useTranslate();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        p: 4,
        border: `1px dashed ${color.panelBorder}`,
        borderRadius: borderRadius.sm,
        color: color.gray,
        textAlign: "center",
      }}
      data-testid="recommendation-empty-state"
    >
      <Inbox sx={{ fontSize: 30, color: color.iconMuted }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {translate("activityGoals.recommendations.empty")}
      </Typography>
      <Typography variant="caption">{translate("activityGoals.recommendations.emptyHint")}</Typography>
      <Button
        size="small"
        startIcon={<Autorenew sx={{ fontSize: typography.fontSize.md }} />}
        onClick={onRegenerate}
        sx={{ mt: 0.5, color: color.secondary }}
      >
        {translate("activityGoals.recommendations.regenerate")}
      </Button>
    </Box>
  );
};

const CategoryRecommendationBlock = ({
  kind,
  userId,
  patientId,
  canCreateNewProcedure,
  goal,
}: {
  kind: ActivityGoalKind;
  userId: string;
  patientId: string;
  canCreateNewProcedure: boolean;
  goal: GoalProgress;
}) => {
  const translate = useTranslate();
  const handleError = useErrorHandler();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [excludeIds, setExcludeIds] = useState<number[]>([]);
  const [status, setStatus] = useState(BlockStatus.Loading);
  const [plannedUuids, setPlannedUuids] = useState<Set<string>>(new Set());
  const [dialogRecommendation, setDialogRecommendation] = useState<Recommendation | null>(null);

  const load = useCallback(
    async (excludeContentIds: number[]) => {
      setStatus(BlockStatus.Loading);
      try {
        const result = await client.query<ActivityRecommendationsQuery, ActivityRecommendationsQueryVariables>({
          query: ActivityRecommendationsDocument,
          variables: { userId, kind, limit: RECOMMENDATION_LIMIT, excludeContentIds },
          fetchPolicy: "network-only",
        });
        setRecommendations(result.data.activityRecommendations ?? []);
        setStatus(BlockStatus.Loaded);
      } catch (error) {
        handleError(error, translate("activityGoals.recommendations.loadError"), {
          operation: "activityRecommendations",
          userId,
          kind,
          limit: RECOMMENDATION_LIMIT,
          excludeContentIds,
        });
        setStatus(BlockStatus.Error);
      }
    },
    [handleError, translate, userId, kind]
  );

  useEffect(() => {
    void load([]);
  }, [load]);

  const handleRegenerate = () => {
    const nextExclude = [...excludeIds, ...recommendations.map(recommendation => recommendation.cmsContentId)];
    setExcludeIds(nextExclude);
    void load(nextExclude);
  };

  const style = activityCategoryStyle[kind];

  return (
    <Box data-testid={`recommendation-block-${kind.toLowerCase()}`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, pb: 1.5 }}>
        <ActivityCategoryIcon kind={kind} size={34} />
        <Typography sx={{ fontSize: typography.fontSize.md, fontWeight: 600 }}>
          {translate(`activityGoals.kind.${kind}`)}
        </Typography>
        <Chip
          size="small"
          icon={<Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: style.color, ml: 1 }} />}
          label={translate("activityGoals.progress.perWeek", {
            done: goal.doneMinutes,
            target: goal.weeklyGoalMinutes,
          })}
          sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 500 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={
            <Autorenew
              sx={{
                fontSize: typography.fontSize.md,
                animation: status === BlockStatus.Loading ? "spin 0.8s linear infinite" : "none",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
          }
          onClick={handleRegenerate}
          disabled={status === BlockStatus.Loading}
          data-testid={`recommendation-regenerate-${kind.toLowerCase()}`}
          sx={{ color: color.secondary, fontWeight: 500 }}
        >
          {translate("activityGoals.recommendations.regenerate")}
        </Button>
      </Box>

      {status === BlockStatus.Loading && (
        <Box>
          {[0, 1, 2].map(index => (
            <RecommendationSkeleton key={index} />
          ))}
        </Box>
      )}

      {status === BlockStatus.Error && (
        <Typography variant="body2" color="error">
          {translate("activityGoals.recommendations.loadError")}
        </Typography>
      )}

      {status === BlockStatus.Loaded && recommendations.length === 0 && (
        <EmptyRecommendations onRegenerate={handleRegenerate} />
      )}

      {status === BlockStatus.Loaded &&
        recommendations.length > 0 &&
        recommendations.map(recommendation => (
          <RecommendationRow
            key={recommendation.cmsContentId}
            recommendation={recommendation}
            kind={kind}
            isPlanned={recommendation.isPlanned || plannedUuids.has(recommendation.contentUuid)}
            onPlan={() => setDialogRecommendation(recommendation)}
          />
        ))}

      {dialogRecommendation && (
        <PlanActivityDialog
          open
          onClose={() => setDialogRecommendation(null)}
          onPlanned={() => setPlannedUuids(prev => new Set(prev).add(dialogRecommendation.contentUuid))}
          recommendation={dialogRecommendation}
          kind={kind}
          userId={userId}
          patientId={patientId}
          canCreateNewProcedure={canCreateNewProcedure}
        />
      )}
    </Box>
  );
};

export const RecommendedActivities = ({ goals }: { goals: ActivityGoalProgressByKind }) => {
  const record = useRecordContext<PatientRecord>();
  const translate = useTranslate();

  const userId = record?.userId;

  if (!userId) return null;

  return (
    <Box sx={{ mt: 4, width: SECTION_WIDTH, maxWidth: "100%" }} data-testid="recommended-activities">
      <Typography variant="h6" sx={{ fontSize: typography.fontSize.xl, fontWeight: 700 }}>
        {translate("activityGoals.recommendations.title")}
      </Typography>
      <Typography variant="body2" sx={{ color: color.gray, mb: 1 }}>
        {translate("activityGoals.recommendations.subtitle")}
      </Typography>
      <Stack spacing={2}>
        {ACTIVITY_GOAL_KINDS.map(kind => (
          <CategoryRecommendationBlock
            key={kind}
            kind={kind}
            userId={userId}
            patientId={record.id}
            canCreateNewProcedure={record.sourceSystem === DataSource.M4C || !record.sourceSystem}
            goal={goals[kind]}
          />
        ))}
      </Stack>
    </Box>
  );
};
